import React from "react";
import {
  AFFILIATE_URL,
  PACKAGES,
  BONUSES,
  INGREDIENTS,
  FAQS,
  HERO_COPY,
  GUARANTEE_COPY,
  HERO_TESTIMONIALS,
  MAIN_TESTIMONIALS,
  CUSTOMER_STRIP_IMAGES,
} from "../mock/mitolynMock";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function TopNav() {
  return (
    <header className="sticky top-0 z-40 bg-white/98 backdrop-blur-sm border-b border-slate-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        <a
          href={AFFILIATE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_fc80331a-96f2-46e1-a59f-214c25fdb97e/artifacts/stqswucd_image.png"
            alt="MITOLYN"
            className="h-8 w-auto"
          />
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-800">
          <button
            type="button"
            onClick={() => scrollToId("bonuses")}
            className="text-slate-800 hover:text-[#F26522] transition-colors"
          >
            Free Bonus
          </button>
          <button
            type="button"
            onClick={() => scrollToId("about")}
            className="text-slate-800 hover:text-[#F26522] transition-colors"
          >
            About
          </button>
          <button
            type="button"
            onClick={() => scrollToId("ingredients")}
            className="text-slate-800 hover:text-[#F26522] transition-colors"
          >
            Ingredients
          </button>
          <button
            type="button"
            onClick={() => scrollToId("faq")}
            className="text-slate-800 hover:text-[#F26522] transition-colors"
          >
            FAQ
          </button>
          <button
            type="button"
            onClick={() => window.open(AFFILIATE_URL, "_blank")}
            className="rounded-full bg-[#F26522] px-6 py-2 text-white font-semibold shadow-md hover:bg-[#ff8147] transition-colors"
          >
            Order Now
          </button>
        </nav>
      </div>
    </header>
  );
}

function HeroStars() {
  return (
    <div className="mt-4 hidden md:block space-y-3">
      {HERO_TESTIMONIALS.map((t) => (
        <div
          key={t.id}
          className="flex items-center gap-3 text-white text-sm"
        >
          <img
            src={t.avatar}
            alt={t.name}
            className="w-10 h-10 rounded-2xl border-2 border-[#00e8fe] object-cover"
          />
          <div>
            <div className="flex items-center gap-1 text-[#ffbf00] text-xs mb-1">
              <img
                src="https://mitolyn.com/assets/images/generic/rating.png"
                alt="5/5 rating"
                className="h-4 w-auto"
              />
              <span className="font-semibold text-white">5/5</span>
            </div>
            <p className="text-xs leading-snug flex items-start gap-1">
              <img
                src="https://mitolyn.com/assets/images/generic/quote_light.png"
                alt="quote"
                className="w-4 h-4 mt-0.5"
              />
              <span>{t.quote}</span>
            </p>
            <p className="text-[11px] mt-1">
              <strong>{t.name}</strong> - Real User Review
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function PackageCard({ pack }) {
  return (
    <a
      href={AFFILIATE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-full"
    >
      <div
        className={`relative h-full rounded-2xl bg-white shadow-xl border border-slate-200 transition-transform duration-200 hover:-translate-y-1 hover:shadow-2xl flex flex-col`}
      >
        {pack.label && (
          <div
            className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-semibold tracking-wide shadow-md ${
              pack.mostPopular
                ? "bg-[#ffed8a] text-slate-900"
                : "bg-slate-900 text-white"
            }`}
          >
            {pack.label}
          </div>
        )}
        <div className="pt-6 pb-6 px-4 flex-1 flex flex-col items-center text-center">
          <div className="mt-2 text-sky-900 font-extrabold tracking-tight text-3xl md:text-4xl">
            {pack.bottles}
          </div>
          <div className="mt-1 text-slate-800 font-semibold text-lg">
            {pack.supply}
          </div>

          <div className="mt-4 hidden md:block">
            <img
              src={pack.desktopImage}
              alt={pack.bottles}
              className="mx-auto max-w-[260px] h-auto"
            />
          </div>
          <div className="mt-4 md:hidden">
            <img
              src={pack.mobileImage}
              alt={pack.bottles}
              className="mx-auto max-w-[220px] h-auto"
            />
          </div>

          <div className="mt-4 text-4xl md:text-5xl font-extrabold text-sky-900">
            {pack.price}
          </div>

          {pack.bonuses.length > 0 && (
            <div className="mt-4 w-full text-left text-sm font-semibold text-slate-900 space-y-1">
              {pack.bonuses.map((b) => (
                <div key={b} className="flex items-center gap-2">
                  <img
                    src="https://mitolyn.com/assets/images/bonus-tick.png"
                    alt="bonus"
                    className="w-4 h-4"
                  />
                  <span>{b}</span>
                </div>
              ))}
              {pack.freeShipping && (
                <div className="flex items-center gap-2">
                  <img
                    src="https://mitolyn.com/assets/images/red-tick.png"
                    alt="free shipping"
                    className="w-4 h-4"
                  />
                  <span>FREE US SHIPPING!</span>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 w-full">
            <img
              src={pack.buttonImage}
              alt="Order now"
              className="w-full max-w-xs mx-auto cursor-pointer"
            />
            <img
              src="https://mitolyn.com/assets/images/generic/cards.png"
              alt="Payment methods"
              className="mt-2 mx-auto max-w-[180px]"
            />
          </div>
        </div>
      </div>
    </a>
  );
}

function HeroSection() {
  return (
    <section className="bg-gradient-to-r from-[#0D50B8] via-[#0D98B8] to-[#0D50B8] text-white pt-16 pb-12 md:pb-16">
      <div className="max-w-6xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
        <div className="order-2 md:order-1 space-y-4 md:space-y-6">
          <p className="text-sm uppercase tracking-[0.25em] text-teal-100">
            {HERO_COPY.eyebrow}
          </p>
          <h1 className="font-extrabold text-3xl sm:text-4xl md:text-5xl leading-tight">
            Unlock Your
            <br />
            Metabolic Power
          </h1>
          <p className="text-xl md:text-2xl font-medium">
            Burn More Calories &amp; Feel Great With <strong>Mitolyn</strong>.
          </p>
          <p className="text-base md:text-lg text-teal-50 max-w-xl">
            {HERO_COPY.subtitle}
          </p>
          <div className="pt-4">
            <a
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-[#ffdd4a] px-8 py-3 text-slate-900 font-semibold text-lg shadow-lg transition-colors duration-200 hover:bg-[#ffe879]"
            >
              Get Started &amp; Claim Discount
            </a>
            {/* Affiliate routing info text removed as requested */}
          </div>
          <HeroStars />
        </div>
        <div className="order-1 md:order-2 flex flex-col items-center gap-4">
          <a
            href={AFFILIATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full max-w-md"
          >
            <img
              src="https://mitolyn.com/assets/images/product-home.png"
              alt="Mitolyn bottles"
              className="w-full h-auto drop-shadow-2xl"
            />
          </a>
          <div className="w-full max-w-lg rounded-2xl overflow-hidden border border-white/20 bg-black/80 shadow-xl">
            <div className="relative w-full pt-[56.25%] bg-black">
              <iframe
                src="https://www.youtube.com/embed/opXdReppkdw?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1"
                title="Mitolyn Official Video"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
              {/* play icon overlay removed as requested */}
            </div>
          </div>
          <div className="w-full max-w-lg flex justify-center mt-3">
            <a
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-[#F26522] px-6 py-2 text-white text-sm font-semibold shadow-md hover:bg-[#ff8147] transition-colors"
            >
              Click here to order now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection({ title, id }) {
  return (
    <section
      id={id}
      className="bg-[#0b4434] bg-gradient-to-b from-[#0b4434] to-[#021218] text-white pt-10 pb-12"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-2xl md:text-3xl font-extrabold leading-tight mb-6">
          {title || (
            <>
              Claim Your Discounted Mitolyn
              <br />
              Below For Huge Savings
            </>
          )}
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {PACKAGES.map((pack) => (
            <PackageCard key={pack.id} pack={pack} />
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-teal-100">
          *96% of customers order 6 bottles (our recommended option)
        </p>
      </div>
    </section>
  );
}

function GuaranteeSection() {
  return (
    <section className="bg-white py-12 md:py-16">
      <div className="max-w-5xl mx-auto px-4 md:px-6 flex flex-col md:flex-row gap-8 items-center">
        <div className="md:w-1/3 flex justify-center">
          <a
            href={AFFILIATE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={GUARANTEE_COPY.badgeImage}
              alt="90-day money back guarantee"
              className="max-w-[260px] w-full h-auto"
            />
          </a>
        </div>
        <div className="md:w-2/3 space-y-4">
          <h2 className="text-center md:text-left text-2xl md:text-3xl font-extrabold uppercase leading-tight text-slate-900">
            {GUARANTEE_COPY.titleLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
          <p className="text-lg text-slate-800 leading-relaxed">
            {GUARANTEE_COPY.body}
          </p>
          <div className="mt-4 flex flex-col items-center gap-3">
            <a
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={GUARANTEE_COPY.logosImage}
                alt="FDA and other assurances"
                className="max-w-xs w-full h-auto"
              />
            </a>
            <p className="text-sm text-slate-600 text-center">
              <strong>Mitolyn is proudly manufactured in the USA</strong> with the finest of
              domestic and foreign ingredients.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function BonusSection() {
  return (
    <section id="bonuses" className="bg-[#02243a] text-white py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-2xl md:text-3xl font-extrabold leading-tight mb-8">
          Order <span className="text-yellow-300">6 Bottles</span> or
          <span className="text-yellow-300"> 3 Bottles</span>
          <br className="hidden md:block" /> and Get <u className="underline decoration-yellow-300">2 FREE Bonuses</u>!
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {BONUSES.map((bonus) => (
            <a
              key={bonus.id}
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl bg-slate-900/40 border border-white/10 p-6 flex flex-col items-center text-center shadow-lg hover:border-yellow-300/70 transition-colors"
            >
              <div className="relative mb-4">
                <img
                  src={bonus.image}
                  alt={bonus.title}
                  className="rounded-xl max-w-xs w-full h-auto shadow-xl"
                />
                <img
                  src="https://mitolyn.com/assets/images/generic/digital.png"
                  alt="Digital download"
                  className="absolute -bottom-3 right-4 w-20"
                />
              </div>
              <div className="text-sm text-sky-200 font-semibold mb-1">
                {bonus.badge}
              </div>
              <h3 className="text-xl font-extrabold mb-2">{bonus.title}</h3>
              <div className="text-yellow-300 font-semibold mb-3">100% FREE</div>
              <p className="text-sm md:text-base text-slate-100 leading-relaxed">
                {bonus.description}
              </p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section id="about" className="bg-white py-12 md:py-16">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <p className="font-bold text-left text-xl md:text-2xl mb-3 text-emerald-900">
          November 2025 - New Scientific Discovery
        </p>
        <h2 className="text-2xl md:text-3xl font-extrabold mb-3 text-slate-900">
          Harvard scientists discover shocking <span className="underline decoration-yellow-400">New Cause</span> of slow
          metabolism...
        </h2>
        <h3 className="text-xl md:text-2xl font-semibold mb-6 text-slate-900 italic">
          and it's not what you think...
        </h3>
        <div className="grid md:grid-cols-[2fr,3fr] gap-8 items-start mb-4">
          <div className="flex justify-center">
            <img
              src="https://mitolyn.com/assets/images/mito.png"
              alt="Mitochondria illustration"
              className="max-w-xs w-full rounded-xl shadow-md"
            />
          </div>
          <div className="space-y-4 text-slate-800 text-sm md:text-base leading-relaxed">
            <p>
              Harvard scientists used cutting-edge microscopic techniques to study 1,700 women and men, and found
              just one common factor in every overweight person: <strong>low mitochondria levels</strong>. They also
              found one thing common in every person who was slim, toned and had smooth young skin:
              <strong> high mitochondria levels</strong>.
            </p>
            <p>
              Like tiny engines, <strong>mitochondria</strong> are the parts inside every cell that make
              <strong> energy</strong> from the food we eat and by burning stored fat. This energy, called ATP, is what
              our body needs for everything – from moving our muscles and pumping our heart to thinking clearly.
            </p>
            <p>
              In short, the more healthy mitochondria you have, <strong>the more fat you can burn</strong> while powering
              every part of your body.
            </p>
          </div>
        </div>
        <p className="text-[11px] text-slate-600 leading-snug">
          References: Leukocyte mitochondrial DNA copy number, anthropometric indices, and weight change in US women;
          Impaired Mitochondrial Biogenesis in Adipose Tissue in Acquired Obesity; Persistent low body weight in humans
          is associated with higher mitochondrial activity in white adipose tissue; Mitochondrial DNA copy number in
          peripheral blood is independently associated with visceral fat accumulation in healthy young adults.
        </p>
      </div>
    </section>
  );
}

function IngredientsSection() {
  return (
    <section id="ingredients" className="bg-white py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-2xl md:text-3xl font-extrabold mb-3 text-slate-900">
          Inside every Mitolyn capsule you'll find:
        </h2>
        <p className="text-center text-base md:text-lg text-slate-700 mb-8">
          A unique proprietary blend of 6 rare ingredients that promote
          <strong> calorie-burning</strong> mitochondria!
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {INGREDIENTS.map((ing) => (
            <a
              key={ing.id}
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full hover:border-teal-500/70 transition-colors"
            >
              <img
                src={ing.image}
                alt={ing.name}
                className="w-full h-52 object-cover"
              />
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="text-xl font-extrabold text-slate-900 text-center mb-1">
                  {ing.name}
                </h3>
                <p className="italic text-sm text-slate-600 mb-4 text-left">
                  {ing.tagline}
                </p>
                <ul className="space-y-1 text-sm text-slate-800">
                  {ing.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <img
                        src="https://mitolyn.com/assets/images/tick-icon.png"
                        alt="tick"
                        className="mt-1 w-3 h-3"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="bg-white py-12 md:py-16">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-2xl md:text-3xl font-extrabold mb-8 text-slate-900">
          Real Mitolyn Users. Real Results.
        </h2>
        <div className="space-y-8">
          {MAIN_TESTIMONIALS.map((t) => (
            <div
              key={t.id}
              className="border border-slate-200 rounded-2xl p-4 md:p-6 shadow-sm bg-white"
            >
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-sky-500"
                />
                <div>
                  <p className="text-lg md:text-xl font-extrabold leading-tight mb-1">
                    {t.title}
                  </p>
                  <div className="flex items-center gap-1 text-[#ffbf00] text-sm">
                    <img
                      src="https://mitolyn.com/assets/images/generic/rating.png"
                      alt="rating"
                      className="h-4 w-auto"
                    />
                    <span className="text-slate-800 font-semibold">
                      Verified Purchase
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-sm md:text-base text-slate-800 leading-relaxed mb-3">
                {t.body}
              </p>
              <p className="text-sm font-semibold text-slate-900 mb-1">
                <strong>{t.name}</strong> - {t.location}
              </p>
              <p className="text-xs md:text-sm text-slate-700 font-semibold">
                {t.package}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 md:gap-3">
          {CUSTOMER_STRIP_IMAGES.map((src) => (
            <a
              key={src}
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block overflow-hidden rounded-xl"
            >
              <img
                src={src}
                alt="Mitolyn customer"
                className="w-full h-full object-cover"
              />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-[#02243a] text-white py-12 md:py-16" id="faq">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-2xl md:text-3xl font-extrabold mb-8">
          Frequently Asked Questions
        </h2>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((faq) => (
            <AccordionItem
              key={faq.id}
              value={String(faq.id)}
              className="border border-white/15 rounded-xl px-4 bg-slate-900/40"
            >
              <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-white">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-slate-100">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function ReferencesSection() {
  return (
    <section className="bg-white py-10">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <p className="text-center text-slate-700 mb-4 italic">
          Scientific References:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {[
            "https://mitolyn.com/assets/images/l1.jpg",
            "https://mitolyn.com/assets/images/l2.jpg",
            "https://mitolyn.com/assets/images/l3.jpg",
            "https://mitolyn.com/assets/images/l4.jpg",
          ].map((src, idx) => (
            <a
              key={src}
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={src}
                alt={`reference ${idx + 1}`}
                className="w-full h-auto"
              />
            </a>
          ))}
        </div>
        <p className="text-[11px] leading-relaxed text-slate-600">
          Scientific citations and detailed references are preserved from the original Mitolyn website and can be
          expanded further if needed.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0b4434] text-white py-6 text-sm">
      <div className="max-w-6xl mx-auto px-4 md:px-6 space-y-4">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <a
            href={AFFILIATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold hover:underline"
          >
            Get Started
          </a>
          <a href="/terms" className="hover:underline">
            Terms
          </a>
          <a href="/privacy" className="hover:underline">
            Privacy
          </a>
        </div>
        <p className="max-w-4xl mx-auto text-[11px] leading-snug text-center text-teal-100">
          Statements found on this website have not been evaluated by the Food and Drug Administration.
          Products on this website are not intended to diagnose, treat, cure, or prevent any disease. If you
          are pregnant, nursing, taking medication, or have a medical condition, consult your physician before
          using our products. ClickBankae is a registered trademark of Click Sales, Inc., a Delaware
          corporation located at 1444 S. Entertainment Ave., Suite 410 Boise, ID 83709, USA and used by
          permission. ClickBank's role as retailer does not constitute an endorsement, approval or review of
          these products or any claim, statement or opinion used in promotion of these products.
        </p>
      </div>
    </footer>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-50">
      <TopNav />
      <HeroSection />
      <PricingSection id="order" />
      <GuaranteeSection />
      <BonusSection />
      <AboutSection />
      <IngredientsSection />
      <TestimonialsSection />
      <FAQSection />
      <PricingSection
        id="order-bottom"
        title="Choose Your Package Below For Huge Savings"
      />
      <GuaranteeSection />
      <ReferencesSection />
      <Footer />
    </div>
  );
}
